import { Fragment, useEffect, useMemo, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { listFinanceiro } from "../services/financeiroService";
import { updateParcela } from "../services/parcelasService";
import StatCard from "../components/ui/StatCard";
import StatusBadge from "../components/ui/StatusBadge";
import Loading, { ErrorBox } from "../components/ui/Loading";
import FinanceiroFormModal from "../components/forms/FinanceiroFormModal";
import { parseLocalDate, diasAtraso } from "../utils/financeiro";
import { formatMoeda } from "../utils/moeda";
import {
  fetchContasReceber,
  fetchContasPagar,
  openBlobInTab,
  downloadBlob,
  extractBlobErrorMessage,
} from "../services/relatoriosService";

// TipoFinanceiro: 0 Receita · 1 Despesa
const TIPO_RECEITA = 0;
const TIPO_DESPESA = 1;

function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

// Date → "YYYY-MM-DD" no fuso local (evita o drift UTC de toISOString).
function isoLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Componente compartilhado pelas telas Contas a Receber e Contas a Pagar.
// Props:
//   tipo: "receber" | "pagar"
//
// PR C2.2: opera sobre PARCELA, não sobre a despesa-mãe. Cada parcela em aberto
// (não-paga) do tipo requisitado vira uma linha (a faculdade 3x = 3 linhas), com
// seu próprio valor/vencimento. Cards somam parcela por moeda. Clique numa linha
// abre o lançamento-mãe no FinanceiroFormModal (o flatten preserva a mãe em `mae`).
export default function ContasListPage({ tipo }) {
  const { C } = useTheme();
  const { profile } = useAuth();
  // PR3 moeda-por-guia: símbolo único da moeda do guia (post-PR2, todo
  // Financeiro do guia já é dessa moeda). Fallback defensivo pra "EUR".
  const moeda = profile?.moeda ?? "EUR";
  const fmt = (v) => formatMoeda(v, moeda);
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // Loading independente por botão de relatório — Visualizar e Baixar podem
  // rodar em paralelo (fetches independentes, sem lock compartilhado).
  const [previewBusy, setPreviewBusy] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);

  // Popover de baixa rápida da linha. Guarda a parcela ativa + data escolhida
  // (default: hoje). Só uma linha por vez — abrir outra fecha a anterior.
  // Sem valor customizado: baixa integral (parcial fica pra PR2 quando o caso
  // aparecer).
  const [pagando, setPagando] = useState(null); // { parcelaId, financeiroId, valor, vencimento, data, saving, error }

  function abrirPopover(r, e) {
    e.stopPropagation();
    setPagando({
      parcelaId: r.parcelaId,
      financeiroId: r.mae.id,
      valor: r.valor,
      vencimento: r.vencimento,
      data: isoLocal(new Date()),
      saving: false,
      error: null,
    });
  }

  function fecharPopover(e) {
    e?.stopPropagation();
    setPagando(null);
  }

  async function confirmarPagamento(e) {
    e?.stopPropagation();
    if (!pagando || pagando.saving) return;
    setPagando((p) => ({ ...p, saving: true, error: null }));
    try {
      await updateParcela(pagando.financeiroId, pagando.parcelaId, {
        valor: pagando.valor,
        vencimento: pagando.vencimento,
        pagoEm: pagando.data,
        pago: true,
      });
      setPagando(null);
      await reload();
    } catch (err) {
      setPagando((p) => ({
        ...p,
        saving: false,
        error: err?.response?.data?.message || "Erro ao marcar como paga.",
      }));
    }
  }
  // Filtro de período (parcelas que vencem em [de, ate]). Vazio = sem filtro =
  // comportamento atual (todas as não-pagas).
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");

  const isReceber = tipo === "receber";
  const tipoFin = isReceber ? TIPO_RECEITA : TIPO_DESPESA;
  const pageTitle = isReceber ? "Contas a Receber" : "Contas a Pagar";
  const pageSubtitle = isReceber
    ? "Receitas em aberto (pendentes ou recebidas parcialmente)"
    : "Despesas em aberto (pendentes ou pagas parcialmente)";
  const cardTotalLabel = isReceber ? "Total a receber" : "Total a pagar";
  const cardTotalIcon = isReceber ? "💶" : "💸";
  const cardMesLabel = isReceber ? "Recebido no mês" : "Pago no mês";
  const cardMesIcon = isReceber ? "✅" : "💳";
  const statusKind = isReceber ? "financeiro_receita" : "financeiro_despesa";

  // Período ativo só quando AMBAS as datas estão preenchidas. Swap silencioso se
  // de > ate (mais simples que bloquear; evita resultado vazio confuso).
  const periodoAtivo = !!(dataInicio && dataFim);
  const periodoDe = periodoAtivo ? (dataInicio <= dataFim ? dataInicio : dataFim) : "";
  const periodoAte = periodoAtivo ? (dataInicio <= dataFim ? dataFim : dataInicio) : "";

  // Atalhos — calculam as duas datas em ISO local (sem drift de fuso). "Próximos
  // 3 meses" começa no 1º dia do mês atual (mais previsível que "hoje").
  function setPeriodoEsteMes() {
    const h = new Date();
    setDataInicio(isoLocal(new Date(h.getFullYear(), h.getMonth(), 1)));
    setDataFim(isoLocal(new Date(h.getFullYear(), h.getMonth() + 1, 0)));
  }
  function setPeriodoProximoMes() {
    const h = new Date();
    setDataInicio(isoLocal(new Date(h.getFullYear(), h.getMonth() + 1, 1)));
    setDataFim(isoLocal(new Date(h.getFullYear(), h.getMonth() + 2, 0)));
  }
  function setPeriodoProximos3Meses() {
    const h = new Date();
    setDataInicio(isoLocal(new Date(h.getFullYear(), h.getMonth(), 1)));
    setDataFim(isoLocal(new Date(h.getFullYear(), h.getMonth() + 3, 0)));
  }
  function limparPeriodo() {
    setDataInicio("");
    setDataFim("");
  }

  // Estilos da barra de filtro (pegada dos filtros existentes — Admin/C2.2).
  const dateInputStyle = {
    padding: "8px 12px",
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    background: C.inputBg,
    color: C.text,
    fontSize: 13,
    outline: "none",
    fontFamily: "'DM Sans', sans-serif",
  };
  const atalhoBtnStyle = {
    padding: "8px 12px",
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    background: "transparent",
    color: C.textMuted,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
  };

  async function reload() {
    setLoading(true);
    try {
      const res = await listFinanceiro();
      setLista(res);
      setError(null);
    } catch {
      setError("Não foi possível carregar os lançamentos.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  // Fetcher certo por tipo — escolhido uma vez na renderização dos handlers.
  const fetchRelatorio = isReceber ? fetchContasReceber : fetchContasPagar;

  async function handleVisualizarRelatorio() {
    if (previewBusy) return;
    // Pre-abre a aba ANTES do await pra não ser bloqueado como popup
    // não-solicitado. Mesma técnica do PDF de roteiro em RoteirosPage.
    const newTab = window.open("", "_blank");
    if (!newTab) {
      alert("Permita pop-ups pra visualizar o PDF.");
      return;
    }
    setPreviewBusy(true);
    try {
      const { blob } = await fetchRelatorio(
        periodoAtivo ? { de: periodoDe, ate: periodoAte } : {}
      );
      openBlobInTab(newTab, blob);
    } catch (err) {
      newTab.close();
      const msg = (await extractBlobErrorMessage(err)) ?? "Erro ao gerar relatório.";
      alert(msg);
    } finally {
      setPreviewBusy(false);
    }
  }

  async function handleBaixarRelatorio() {
    if (downloadBusy) return;
    setDownloadBusy(true);
    try {
      const { blob, filename } = await fetchRelatorio(
        periodoAtivo ? { de: periodoDe, ate: periodoAte } : {}
      );
      downloadBlob(blob, filename);
    } catch (err) {
      const msg = (await extractBlobErrorMessage(err)) ?? "Erro ao gerar relatório.";
      alert(msg);
    } finally {
      setDownloadBusy(false);
    }
  }

  // ─── Flatten parcela → linha ───
  // Cada parcela NÃO-paga de um lançamento do tipo certo vira uma linha. Carrega
  // a mãe inteira (`mae`) pra o clique abrir o modal de edição do lançamento.
  // Rótulo: "Entrada" pra parcela de entrada (receita de roteiro); "n/N" quando
  // há >1 parcela não-entrada; sem rótulo pra à-vista (1 parcela = idêntico a hoje).
  const rows = useMemo(() => {
    const out = [];
    for (const f of lista) {
      if (f.tipo !== tipoFin) continue;
      const parcelas = f.parcelas ?? [];
      const totalNaoEntrada = parcelas.filter((p) => !p.ehEntrada).length;
      for (const p of parcelas) {
        if (p.pago) continue; // só parcelas em aberto
        // Filtro de período: só parcelas que vencem em [periodoDe, periodoAte]
        // (comparação lexical de string ISO — sem new Date(), sem fuso).
        if (periodoAtivo && (p.vencimento < periodoDe || p.vencimento > periodoAte))
          continue;
        out.push({
          parcelaId: p.id,
          valor: Number(p.valor) || 0,
          vencimento: p.vencimento,
          ehEntrada: p.ehEntrada,
          rotulo: p.ehEntrada
            ? "Entrada"
            : totalNaoEntrada > 1
            ? `${p.numero}/${totalNaoEntrada}`
            : null,
          mae: f,
        });
      }
    }
    return out;
  }, [lista, tipoFin, periodoAtivo, periodoDe, periodoAte]);

  // ─── Ordenação (por vencimento da PARCELA) ───
  // 1) Atrasadas (vencimento < hoje), crescente (mais antiga = mais prioritária).
  // 2) Próximas (vencimento >= hoje), crescente.
  // 3) Sem vencimento no fim (defensivo — parcela tem vencimento NOT NULL).
  const ordered = useMemo(() => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const hojeMs = hoje.getTime();

    const atrasadas = [];
    const proximas = [];
    const semVencimento = [];

    for (const r of rows) {
      if (!r.vencimento) {
        semVencimento.push(r);
        continue;
      }
      const v = parseLocalDate(r.vencimento);
      v.setHours(0, 0, 0, 0);
      if (v.getTime() < hojeMs) atrasadas.push(r);
      else proximas.push(r);
    }

    // ISO "YYYY-MM-DD" ordena bem lexicamente — evita reparse.
    atrasadas.sort((a, b) => a.vencimento.localeCompare(b.vencimento));
    proximas.sort((a, b) => a.vencimento.localeCompare(b.vencimento));

    return [...atrasadas, ...proximas, ...semVencimento];
  }, [rows]);

  // ─── Totais dos cards (sobre PARCELA, moeda única do guia post-PR2) ───
  const totals = useMemo(() => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const hojeMs = hoje.getTime();
    const em7diasMs = hojeMs + 7 * 86400000;
    const anoAtual = hoje.getFullYear();
    const mesAtual = hoje.getMonth();

    let total = 0;
    let vencidas = 0;
    let vence7 = 0;
    let mes = 0;
    let countAtrasadas = 0;

    for (const f of lista) {
      if (f.tipo !== tipoFin) continue;
      for (const p of f.parcelas ?? []) {
        const valor = Number(p.valor) || 0;

        if (!p.pago) {
          // Em aberto → Total + Vencidas/Vence7 (só a parcela conta, não o saldo inteiro).
          total += valor;

          if (!p.vencimento) continue;
          const v = parseLocalDate(p.vencimento);
          v.setHours(0, 0, 0, 0);
          const vMs = v.getTime();
          if (vMs < hojeMs) {
            countAtrasadas++;
            vencidas += valor;
          } else if (vMs <= em7diasMs) {
            vence7 += valor;
          }
        } else if (p.pagoEm) {
          // Pago/Recebido no mês = parcelas com pagoEm no mês corrente.
          const d = parseLocalDate(p.pagoEm);
          if (d && d.getFullYear() === anoAtual && d.getMonth() === mesAtual) {
            mes += valor;
          }
        }
      }
    }

    return { total, vencidas, vence7, mes, countAtrasadas };
  }, [lista, tipoFin]);

  // ─── Totais do PERÍODO — derivados do MESMO array que vira a lista (rows) ───
  // Fonte ÚNICA: a contagem ("N parcelas") e os valores somados saem do mesmo
  // conjunto, então "X · N" nunca diverge da lista (sem segundo loop com
  // condição própria). Só usados sob filtro. Mono-moeda do guia (PR3).
  const periodoTotals = useMemo(() => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const hojeMs = hoje.getTime();
    let periodo = 0;
    let periodoVencidas = 0;
    let countPeriodoVencidas = 0;
    for (const r of rows) {
      periodo += r.valor;
      if (!r.vencimento) continue;
      const v = parseLocalDate(r.vencimento);
      v.setHours(0, 0, 0, 0);
      if (v.getTime() < hojeMs) {
        countPeriodoVencidas++;
        periodoVencidas += r.valor;
      }
    }
    return { periodo, periodoVencidas, countPeriodoVencidas };
  }, [rows]);

  return (
    <div>
      <div
        style={{
          marginBottom: 24,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>
            {pageTitle}
          </h1>
          <p style={{ color: C.textMuted, fontSize: 13, margin: "4px 0 0" }}>
            {periodoAtivo
              ? `Mostrando parcelas que vencem entre ${fmtDate(periodoDe)} e ${fmtDate(periodoAte)}`
              : pageSubtitle}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={handleVisualizarRelatorio}
            disabled={previewBusy}
            title="Abrir PDF em nova aba"
            style={{
              padding: "10px 16px",
              borderRadius: 10,
              border: "none",
              background: C.accent,
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              cursor: previewBusy ? "wait" : "pointer",
              fontFamily: "'DM Sans', sans-serif",
              opacity: previewBusy ? 0.6 : 1,
            }}
          >
            {previewBusy ? "Carregando..." : "👁️ Visualizar Relatório"}
          </button>
          <button
            type="button"
            onClick={handleBaixarRelatorio}
            disabled={downloadBusy}
            title="Baixar PDF do relatório"
            style={{
              padding: "10px 16px",
              borderRadius: 10,
              border: `1px solid ${C.accent}`,
              background: "transparent",
              color: C.accent,
              fontSize: 12,
              fontWeight: 600,
              cursor: downloadBusy ? "wait" : "pointer",
              fontFamily: "'DM Sans', sans-serif",
              opacity: downloadBusy ? 0.6 : 1,
            }}
          >
            {downloadBusy ? "Baixando..." : "⬇ Baixar PDF"}
          </button>
        </div>
      </div>

      {/* ─── Filtro de período ─── */}
      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 18,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <label style={{ fontSize: 12, color: C.textMuted }}>De</label>
          <input
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
            style={dateInputStyle}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <label style={{ fontSize: 12, color: C.textMuted }}>Até</label>
          <input
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
            style={dateInputStyle}
          />
        </div>
        <button type="button" onClick={setPeriodoEsteMes} style={atalhoBtnStyle}>
          Este mês
        </button>
        <button type="button" onClick={setPeriodoProximoMes} style={atalhoBtnStyle}>
          Próximo mês
        </button>
        <button type="button" onClick={setPeriodoProximos3Meses} style={atalhoBtnStyle}>
          Próximos 3 meses
        </button>
        <button type="button" onClick={limparPeriodo} style={atalhoBtnStyle}>
          Limpar
        </button>

        {periodoAtivo && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              marginLeft: 4,
              padding: "6px 12px",
              borderRadius: 999,
              background: C.accentDim,
              color: C.accent,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            📅 {fmtDate(periodoDe)} – {fmtDate(periodoAte)}
            <span
              onClick={limparPeriodo}
              title="Limpar filtro de período"
              style={{ cursor: "pointer", fontWeight: 700 }}
            >
              ✕
            </span>
          </span>
        )}
      </div>

      {/* ─── Cards (adaptativos: 4 de triagem sem filtro · 2 de período com filtro) ─── */}
      <div style={{ display: "flex", gap: 14, marginBottom: 22, flexWrap: "wrap" }}>
        {periodoAtivo ? (
          <>
            <StatCard
              icon={cardTotalIcon}
              label={isReceber ? "A receber no período" : "A pagar no período"}
              value={fmt(periodoTotals.periodo)}
              sub={`${ordered.length} parcela${ordered.length === 1 ? "" : "s"}`}
              color={C.accent}
            />
            <StatCard
              icon="⚠️"
              label="Já vencidas no período"
              value={fmt(periodoTotals.periodoVencidas)}
              sub={`${periodoTotals.countPeriodoVencidas} parcela${periodoTotals.countPeriodoVencidas === 1 ? "" : "s"}`}
              color={C.red}
            />
          </>
        ) : (
          <>
            <StatCard
              icon={cardTotalIcon}
              label={cardTotalLabel}
              value={fmt(totals.total)}
              sub={`${ordered.length} parcela${ordered.length === 1 ? "" : "s"} em aberto`}
              color={C.accent}
            />
            <StatCard
              icon="⚠️"
              label="Vencidas"
              value={fmt(totals.vencidas)}
              sub={`${totals.countAtrasadas} parcela${totals.countAtrasadas === 1 ? "" : "s"}`}
              color={C.red}
            />
            <StatCard
              icon="⏳"
              label="Vence em 7 dias"
              value={fmt(totals.vence7)}
              sub="Próximos 7 dias"
              color={C.accent}
            />
            <StatCard
              icon={cardMesIcon}
              label={cardMesLabel}
              value={fmt(totals.mes)}
              sub="Mês corrente"
              color={C.green}
            />
          </>
        )}
      </div>

      <FinanceiroFormModal
        open={modalOpen}
        lancamento={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSaved={reload}
        onDeleted={reload}
      />

      {loading && <Loading />}
      {error && <ErrorBox message={error} />}

      {!loading && !error && (
        <div
          style={{
            background: C.bgCard,
            borderRadius: 14,
            border: `1px solid ${C.border}`,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "40px 2fr 1fr 1fr 1fr 130px",
              padding: "12px 20px",
              borderBottom: `1px solid ${C.border}`,
              gap: 8,
            }}
          >
            {["", "Descrição", "Valor", "Vencimento", "Status", ""].map(
              (h, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.textDim,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                  }}
                >
                  {h}
                </span>
              )
            )}
          </div>

          {ordered.map((r) => {
            const f = r.mae;
            const dias = r.vencimento ? diasAtraso(r.vencimento) : null;
            const atrasado = dias !== null && dias > 0;
            const popoverAberto = pagando?.parcelaId === r.parcelaId;
            return (
              <Fragment key={r.parcelaId}>
              <div
                onClick={() => {
                  setEditing(f);
                  setModalOpen(true);
                }}
                style={{
                  display: "grid",
                  gridTemplateColumns: "40px 2fr 1fr 1fr 1fr 130px",
                  padding: "14px 20px",
                  borderBottom: `1px solid ${C.border}`,
                  alignItems: "center",
                  cursor: "pointer",
                  gap: 8,
                  // Destaque de linha pra atrasados — redDim é bem sutil
                  // (rgba(...,0.08-0.12)) e sinaliza sem afogar a lista.
                  background: atrasado ? C.redDim : "transparent",
                }}
              >
                {/* Ícone do tipo */}
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: isReceber ? C.greenDim : C.redDim,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    color: isReceber ? C.green : C.red,
                  }}
                >
                  {isReceber ? "↑" : "↓"}
                </div>
                {/* Descrição — categoria/cliente/roteiro da mãe + rótulo da parcela */}
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: C.text,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isReceber && f.roteiroId && (
                      <span title="Receita de roteiro" style={{ marginRight: 6 }}>
                        🗺️
                      </span>
                    )}
                    {isReceber
                      ? f.roteiroId
                        ? f.clienteNome || f.roteiroTitulo
                        : f.categoria || "Receita avulsa"
                      : f.categoria || "Despesa"}
                    {r.rotulo && (
                      <span
                        style={{
                          marginLeft: 6,
                          fontSize: 10,
                          fontWeight: 700,
                          color: C.textMuted,
                          background: C.bgCardHover,
                          padding: "1px 6px",
                          borderRadius: 5,
                        }}
                      >
                        {r.rotulo}
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: C.textMuted,
                      marginTop: 2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isReceber
                      ? f.roteiroId
                        ? f.roteiroTitulo || f.descricao || f.formaPagamento || "—"
                        : f.descricao || f.clienteNome || f.formaPagamento || "—"
                      : f.descricao || f.formaPagamento || "—"}
                  </div>
                </div>
                {/* Valor da parcela */}
                <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                  {fmt(r.valor)}
                </span>
                {/* Vencimento da parcela — destaca em vermelho se atrasado */}
                <span
                  style={{
                    fontSize: 12,
                    color: atrasado ? C.red : C.textMuted,
                    fontWeight: atrasado ? 600 : 400,
                  }}
                >
                  {fmtDate(r.vencimento)}
                </span>
                {/* Status — "Atrasado há N dias" ou Pendente/A receber (parcela em aberto) */}
                {atrasado ? (
                  <span
                    style={{
                      background: C.redDim,
                      color: C.red,
                      padding: "8px 10px",
                      borderRadius: 6,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 0.3,
                      whiteSpace: "nowrap",
                      textAlign: "center",
                    }}
                  >
                    Atrasado há {dias} {dias === 1 ? "dia" : "dias"}
                  </span>
                ) : (
                  <StatusBadge status={0} kind={statusKind} />
                )}
                {/* Baixa rápida — botão ✓ direto na linha. Abre popover embaixo
                    com data (default hoje) e valor; não abre o modal grande. */}
                <button
                  onClick={(e) => abrirPopover(r, e)}
                  title={isReceber ? "Marcar como recebida" : "Marcar como paga"}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: `1px solid ${C.green}`,
                    background: popoverAberto ? C.green : "transparent",
                    color: popoverAberto ? "#fff" : C.green,
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: "'DM Sans', sans-serif",
                    whiteSpace: "nowrap",
                  }}
                >
                  ✓ {isReceber ? "Receber" : "Pagar"}
                </button>
              </div>

              {popoverAberto && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    padding: "14px 20px",
                    background: C.bgCardHover,
                    borderBottom: `1px solid ${C.border}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontSize: 12, color: C.textMuted }}>
                    {isReceber ? "Recebida em" : "Paga em"}
                  </span>
                  <input
                    type="date"
                    value={pagando.data}
                    onChange={(e) =>
                      setPagando((p) => ({ ...p, data: e.target.value }))
                    }
                    style={{
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: `1px solid ${C.border}`,
                      background: C.inputBg,
                      color: C.text,
                      fontSize: 12,
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  />
                  <span
                    style={{
                      fontSize: 12,
                      color: C.textMuted,
                      marginLeft: 4,
                    }}
                  >
                    Valor:{" "}
                    <strong style={{ color: C.text }}>
                      {fmt(pagando.valor)}
                    </strong>
                  </span>
                  {pagando.error && (
                    <span style={{ fontSize: 11, color: C.red, flex: 1 }}>
                      ⚠️ {pagando.error}
                    </span>
                  )}
                  <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                    <button
                      type="button"
                      onClick={fecharPopover}
                      disabled={pagando.saving}
                      style={{
                        padding: "8px 14px",
                        borderRadius: 8,
                        border: `1px solid ${C.border}`,
                        background: "transparent",
                        color: C.textMuted,
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: pagando.saving ? "wait" : "pointer",
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={confirmarPagamento}
                      disabled={pagando.saving || !pagando.data}
                      style={{
                        padding: "8px 16px",
                        borderRadius: 8,
                        border: "none",
                        background: C.green,
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: pagando.saving ? "wait" : "pointer",
                        fontFamily: "'DM Sans', sans-serif",
                        opacity: pagando.saving ? 0.7 : 1,
                      }}
                    >
                      {pagando.saving ? "Salvando…" : "Confirmar"}
                    </button>
                  </div>
                </div>
              )}
              </Fragment>
            );
          })}

          {ordered.length === 0 && (
            <div
              style={{
                padding: 24,
                fontSize: 13,
                color: C.textMuted,
                textAlign: "center",
              }}
            >
              {periodoAtivo
                ? "Nenhuma parcela vence nesse período."
                : isReceber
                ? "Nenhuma conta a receber em aberto. 🎉"
                : "Nenhuma conta a pagar em aberto. 🎉"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
